import * as React from 'react';
import { useState } from 'react';
import { TTaskAssigneeOption, TTaskStatusOption } from '../../types/Tasks';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import { Theme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import AssigneeChangeModal from './AssigneeChangeModal';

interface ITaskUpdateSectionProps {
  statuses: TTaskStatusOption[];
  assignees: TTaskAssigneeOption[];
  selectedStatusId: number | null;
  selectedAssigneeEmail: string | null;
  onStatusChange: (statusId: number | null) => void;
  onAssigneeChange: (
    assigneeEmail: string | null,
    notifyAssignee: boolean,
    comment: string
  ) => void;
  readOnly?: boolean;
  statusReadOnly?: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    formControl: {
      marginBottom: theme.spacing(2),
      width: '100%',
    },
    colorDot: {
      width: 12,
      height: 12,
      borderRadius: '50%',
      display: 'inline-block',
      marginRight: theme.spacing(1),
      verticalAlign: 'middle',
    },
    assigneeRow: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      marginBottom: theme.spacing(2),
    },
    assigneeField: {
      flex: 1,
    },
  })
);

const TaskUpdateSection: React.FC<ITaskUpdateSectionProps> = ({
  statuses,
  assignees,
  selectedStatusId,
  selectedAssigneeEmail,
  onStatusChange,
  onAssigneeChange,
  readOnly = false,
  statusReadOnly = false,
}): JSX.Element => {
  const classes = useStyles({});
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);

  const assigneeName =
    assignees.find((a) => a.email === selectedAssigneeEmail)?.name ?? '';

  return (
    <Box>
      <FormControl variant="outlined" className={classes.formControl}>
        <InputLabel>{t('tasks.detail.status')}</InputLabel>
        <Select
          value={selectedStatusId ?? ''}
          onChange={(e) =>
            onStatusChange(e.target.value ? Number(e.target.value) : null)
          }
          label={t('tasks.detail.status')}
          disabled={readOnly || statusReadOnly}
        >
          {statuses.map((status) => (
            <MenuItem key={status.id} value={status.id}>
              {status.color && (
                <span
                  className={classes.colorDot}
                  style={{ backgroundColor: status.color }}
                />
              )}
              {status.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box className={classes.assigneeRow}>
        <TextField
          className={classes.assigneeField}
          label={t('tasks.detail.assignee')}
          variant="outlined"
          value={assigneeName}
          disabled
        />
        {!readOnly && (
          <Button
            variant="outlined"
            onClick={() => setModalOpen(true)}
          >
            {t('tasks.detail.change')}
          </Button>
        )}
      </Box>

      <AssigneeChangeModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onApply={onAssigneeChange}
        assignees={assignees}
        currentAssigneeEmail={selectedAssigneeEmail}
      />
    </Box>
  );
};

export default TaskUpdateSection;
